import { useState } from 'react';
import { aboutData } from '@/mocks/about';

export default function ContactSection() {
  const { contact } = aboutData;
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qsv2fnahb7382ovf9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setFormStatus('error');
        setFormMessage('提交失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
            {contact.title}
          </h2>
          <p className="text-foreground-500 text-sm">{contact.subtitle}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-5xl mx-auto">
          <div className="lg:w-[380px] flex-shrink-0 space-y-5">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-mail-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</p>
                <p className="text-foreground-500 text-sm">{contact.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-wechat-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm mb-1">微信公众号</p>
                <p className="text-foreground-500 text-sm">{contact.wechat}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                <i className="ri-weibo-line text-secondary-600 text-lg"></i>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm mb-1">微博</p>
                <p className="text-foreground-500 text-sm">{contact.weibo}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-time-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm mb-1">工作时间</p>
                <p className="text-foreground-500 text-sm">{contact.workingHours}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-map-pin-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm mb-1">公司地址</p>
                <p className="text-foreground-500 text-sm">{contact.address}</p>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70"
            >
              <h3 className="font-heading font-semibold text-foreground-900 mb-6">发送留言</h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
              </div>

              {formMessage && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  formStatus === 'success'
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'bg-accent-50 text-accent-800 border border-accent-200'
                }`}>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="mt-6 w-full py-3 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    发送留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}